/**
 * Part 11.3 - the site switcher, persistent in the left rail.
 *
 * Choosing a site here scopes every page to it; the choice is kept per browser.
 */

import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { NavLink } from 'react-router-dom'

import { api } from '../api/client'
import { useAppStore } from '../store/useAppStore'
import { hazardColour } from './SitesMap'

interface SitesPayload {
  default: string
  run_site: string
  sites: { id: string; display_name: string; hazard_class: string; india_region: string; dem_available: boolean }[]
}

export function SiteRail() {
  const sites = useQuery<SitesPayload>({ queryKey: ['sites'], queryFn: () => api.raw('/sites') })
  const runs = useQuery({ queryKey: ['runs'], queryFn: api.runs })
  const withRuns = new Set((runs.data ?? []).filter((r) => r.status === 'done' && r.site_id).map((r) => r.site_id as string))
  const active = useAppStore((s) => s.activeSiteId)
  const setActive = useAppStore((s) => s.setActiveSiteId)

  // A stored choice that is no longer a registered site falls back to the default.
  useEffect(() => {
    if (sites.data && !sites.data.sites.some((s) => s.id === active)) setActive(sites.data.default)
  }, [sites.data, active, setActive])

  const link = ({ isActive }: { isActive: boolean }) =>
    `block rounded px-2 py-1 text-12 ${isActive ? 'bg-slate-800 text-slate-100' : 'text-slate-400 hover:bg-slate-800/60'}`
  return (
    <aside className="hidden w-44 shrink-0 overflow-y-auto border-r border-slate-800 bg-slate-900/60 p-2 md:block" data-testid="site-rail">
      <div className="label mb-1">Site</div>
      {(sites.data?.sites ?? []).filter((s) => withRuns.has(s.id)).map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => setActive(s.id)}
          data-site={s.id}
          className={`mb-1 w-full rounded border px-2 py-1.5 text-left text-12 ${
            s.id === active ? 'border-sky-600 bg-sky-950 text-sky-100' : 'border-slate-800 text-slate-300 hover:border-slate-600'
          }`}
        >
          <span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full" style={{ background: hazardColour(s.hazard_class) }} />
          {s.display_name}
          <span className="block text-12 text-slate-500">
            {s.hazard_class === 'DAM_FAILURE' ? '▲ dam failure' : '≈ flash flood'}
          </span>
        </button>
      ))}
      {sites.isError && <p className="text-12 text-rose-400">{String(sites.error)}</p>}
      <div className="mt-3 space-y-0.5 border-t border-slate-800 pt-2">
        <NavLink to={`/site/${active}`} className={link}>
          Site dashboard
        </NavLink>
        <NavLink to="/overview" className={link}>
          Sites
        </NavLink>
      </div>
    </aside>
  )
}
